import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Breadcrumbs, BreadcrumbItem, SkeletonCard } from '../components/ui';
import type { DashboardMetrics } from '../types';
import {
  Activity,
  CheckCircle2,
  Clock,
  DollarSign,
  Users,
  Bell,
  Layers,
  Zap,
  TrendingUp,
  AlertCircle,
  ArrowRight,
  LifeBuoy,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

export const Dashboard: React.FC = () => {
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('7d');

  const { data: metrics, isLoading } = useQuery({
    queryKey: ['dashboard-metrics', period],
    queryFn: async () => {
      const response = await api.get<DashboardMetrics>(
        `/admin/analytics/dashboard?period=${period}`
      );
      return response.data;
    },
  });

  const { data: pendingClientsCount = 0 } = useQuery({
    queryKey: ['dashboard-pending-clients'],
    queryFn: async () => {
      try {
        const response = await api.get('/admin/clients/pending');
        const list = response.data?.data || response.data || [];
        return Array.isArray(list) ? list.length : 0;
      } catch {
        return 0;
      }
    },
  });

  const { data: pendingSubscriptionsCount = 0 } = useQuery({
    queryKey: ['dashboard-pending-subscriptions'],
    queryFn: async () => {
      try {
        const response = await api.get('/admin/subscriptions', {
          params: { status: 'pending_payment', limit: 100 },
        });
        const list = response.data?.data || response.data || [];
        return Array.isArray(list) ? list.length : 0;
      } catch {
        return 0;
      }
    },
    refetchInterval: 30000,
  });

  const { data: openTicketsCount = 0 } = useQuery({
    queryKey: ['dashboard-open-tickets'],
    queryFn: async () => {
      try {
        const response = await api.get('/admin/support-tickets', {
          params: { status: 'open', limit: 100 },
        });
        const list = response.data?.data || response.data || [];
        return Array.isArray(list) ? list.length : 0;
      } catch {
        return 0;
      }
    },
    refetchInterval: 30000,
  });

  const [realtimeData, setRealtimeData] = useState<{
    requests_per_minute: number;
    request_history: { time: string; requests: number }[];
  }>({ requests_per_minute: 0, request_history: [] });

  useEffect(() => {
    // Create SSE connection to analytics service via Kong Gateway
    const rawBase = String(api.defaults.baseURL || '')
      .replace(/\/portal\/?$/, '')
      .replace(/\/+$/, '');
    const sseUrl = `${rawBase}/analytics/api/analytics/realtime`;
    const source = new EventSource(sseUrl);

    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        setRealtimeData((prev) => {
          const now = new Date(data.timestamp || Date.now());
          const timeLabel = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;

          // Request rate per minute = req/sec * 60
          const rpm = Math.round(data.requests_per_second * 60);

          const newHistory = [...prev.request_history, { time: timeLabel, requests: rpm }].slice(
            -30
          ); // Keep last 30 entries

          return {
            requests_per_minute: rpm,
            request_history: newHistory,
          };
        });
      } catch (err) {
        console.error('Failed to parse SSE data', err);
      }
    };

    return () => {
      source.close();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-8 animate-fade-in">
        <Breadcrumbs>
          <BreadcrumbItem>Início</BreadcrumbItem>
          <BreadcrumbItem>Painel de Controlo</BreadcrumbItem>
        </Breadcrumbs>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="h-8 w-64 bg-slate-200 dark:bg-slate-800 rounded-lg animate-pulse mb-2" />
            <div className="h-4 w-96 bg-slate-200 dark:bg-slate-800 rounded-lg animate-pulse" />
          </div>
        </div>

        {/* Loading KPI Skeletons */}
        <SkeletonCard count={8} />

        {/* Loading Chart Skeletons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-72 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 animate-pulse" />
          <div className="h-72 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 animate-pulse" />
        </div>
      </div>
    );
  }

  const kpis = metrics
    ? [
        {
          label: 'Total de Pedidos',
          value: metrics.total_requests.toLocaleString(),
          subtext: `${metrics.successful_requests.toLocaleString()} com sucesso`,
          subtextClass: 'text-emerald-600 dark:text-emerald-400',
          icon: Activity,
          iconBg: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
        },
        {
          label: 'Taxa de Sucesso',
          value:
            metrics.total_requests > 0
              ? ((metrics.successful_requests / metrics.total_requests) * 100).toFixed(1) + '%'
              : '0%',
          subtext: `${metrics.failed_requests.toLocaleString()} falhados`,
          subtextClass:
            metrics.failed_requests > 0
              ? 'text-rose-600 dark:text-rose-400'
              : 'text-emerald-600 dark:text-emerald-400',
          icon: CheckCircle2,
          iconBg: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
        },
        {
          label: 'Tempo Médio de Resposta',
          value: `${metrics.average_response_time.toFixed(0)}ms`,
          subtext: 'Média de latência global',
          subtextClass: 'text-slate-500 dark:text-slate-400',
          icon: Clock,
          iconBg: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
        },
        {
          label: 'Receita Total',
          value: `$${metrics.total_revenue.toLocaleString()}`,
          subtext: 'Faturação consolidada',
          subtextClass: 'text-emerald-600 dark:text-emerald-400',
          icon: DollarSign,
          iconBg: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
        },
        {
          label: 'Clientes Ativos',
          value: (metrics.top_clients_by_volume?.length || 0).toLocaleString(),
          subtext: 'Com tráfego no período',
          subtextClass: 'text-slate-500 dark:text-slate-400',
          icon: Users,
          iconBg: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
        },
        {
          label: 'Registos Pendentes',
          value: pendingClientsCount.toLocaleString(),
          subtext: pendingClientsCount > 0 ? 'Aguardam aprovação' : 'Todos os clientes aprovados',
          subtextClass:
            pendingClientsCount > 0
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-slate-500 dark:text-slate-400',
          icon: Bell,
          iconBg: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
        },
        {
          label: 'Subscrições Pendentes',
          value: pendingSubscriptionsCount.toLocaleString(),
          subtext:
            pendingSubscriptionsCount > 0
              ? 'Aguardam validação ou pagamento'
              : 'Nenhuma subscrição pendente',
          subtextClass:
            pendingSubscriptionsCount > 0
              ? 'text-amber-600 dark:text-amber-400 font-bold'
              : 'text-slate-500 dark:text-slate-400',
          icon: Layers,
          iconBg: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
        },
        {
          label: 'Subscrições Ativas',
          value: (
            (metrics as any).active_subscriptions ??
            metrics.endpoint_performance?.length ??
            0
          ).toLocaleString(),
          subtext: 'Endpoints com consumo ativo',
          subtextClass: 'text-slate-500 dark:text-slate-400',
          icon: Layers,
          iconBg: 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400',
        },
        {
          label: 'Tráfego em Tempo Real',
          value: `${realtimeData?.requests_per_minute || 0}/min`,
          subtext: 'Atualizado via Server-Sent Events',
          subtextClass: 'text-primary-600 dark:text-primary-400',
          icon: Zap,
          iconBg: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400',
        },
      ]
    : [];

  return (
    <div className="space-y-8 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem>Início</BreadcrumbItem>
        <BreadcrumbItem>Painel de Controlo</BreadcrumbItem>
      </Breadcrumbs>

      {/* Header and Period Filter */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <TrendingUp className="w-8 h-8 text-primary-500" />
            <span>Painel de Controlo</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
            Métricas de tráfego, latência, faturação e integridade dos serviços
          </p>
        </div>

        {/* Modern Segmented Control */}
        <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold">
          {(['7d', '30d', '90d'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={`px-3.5 py-1.5 rounded-lg transition-all ${
                period === p
                  ? 'bg-white dark:bg-slate-700 text-primary-600 dark:text-primary-400 shadow-2xs font-bold'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              {p === '7d' ? '7 Dias' : p === '30d' ? '30 Dias' : '90 Dias'}
            </button>
          ))}
        </div>
      </div>

      {/* High-Priority Action Alert for Pending Subscriptions */}
      {pendingSubscriptionsCount > 0 && (
        <div className="bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border border-amber-300 dark:border-amber-800/80 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xs">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center text-amber-700 dark:text-amber-300 shrink-0">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <span>Subscrições a Aguardar Confirmação</span>
                <span className="px-2 py-0.5 rounded-full text-xs font-extrabold bg-amber-200 dark:bg-amber-900 text-amber-900 dark:text-amber-100">
                  {pendingSubscriptionsCount}{' '}
                  {pendingSubscriptionsCount === 1 ? 'pendente' : 'pendentes'}
                </span>
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                Existem subscrições com pagamentos bancários pendentes que necessitam de intervenção ou ativação manual.
              </p>
            </div>
          </div>
          <Link
            to="/subscriptions?status=pending_payment"
            className="btn-base btn-primary btn-sm shrink-0 flex items-center gap-1.5 shadow-xs"
          >
            <span>Rever e Ativar Subscrições</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* Action Alert for Open Support Inquiries */}
      {openTicketsCount > 0 && (
        <div className="bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent border border-blue-300 dark:border-blue-800/80 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xs">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-700 dark:text-blue-300 shrink-0">
              <LifeBuoy className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <span>Tickets de Suporte Abertos</span>
                <span className="px-2 py-0.5 rounded-full text-xs font-extrabold bg-blue-200 dark:bg-blue-900 text-blue-900 dark:text-blue-100">
                  {openTicketsCount} {openTicketsCount === 1 ? 'ticket' : 'tickets'}
                </span>
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                Existem pedidos de apoio técnico ou faturação de clientes aguardando resposta operacional.
              </p>
            </div>
          </div>
          <Link
            to="/support?status=open"
            className="btn-base btn-secondary btn-sm shrink-0 flex items-center gap-1.5 shadow-xs"
          >
            <span>Responder a Clientes</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
        {kpis.map((kpi, idx) => {
          const Icon = kpi.icon;
          return (
            <div
              key={idx}
              className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col justify-between"
            >
              <div className="flex items-center justify-between gap-3 mb-3">
                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {kpi.label}
                </span>
                <div className={`p-2.5 rounded-xl ${kpi.iconBg}`}>
                  <Icon className="w-4 h-4" />
                </div>
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                  {kpi.value}
                </p>
                <p
                  className={`text-xs mt-1.5 font-medium flex items-center gap-1 ${kpi.subtextClass}`}
                >
                  {kpi.subtext}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Request Rate Chart */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-xs">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                Taxa de Pedidos em Tempo Real
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Volume de requisições por minuto com atualização contínua
              </p>
            </div>
            <span className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Live
            </span>
          </div>

          {realtimeData?.request_history && (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={realtimeData.request_history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.4} />
                <XAxis dataKey="time" stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    border: '1px solid #334155',
                    borderRadius: '0.75rem',
                    color: '#fff',
                    fontSize: '12px',
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line
                  name="Pedidos/min"
                  type="monotone"
                  dataKey="requests"
                  stroke="#3b82f6"
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 6, fill: '#3b82f6' }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Endpoint Performance */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-xs">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                Latência por Endpoint (Top 5)
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Tempo médio de resposta em milissegundos
              </p>
            </div>
            <Clock className="w-5 h-5 text-slate-400" />
          </div>

          {metrics?.endpoint_performance && metrics.endpoint_performance.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={metrics.endpoint_performance.slice(0, 5)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.4} />
                <XAxis dataKey="endpoint" stroke="#94a3b8" fontSize={10} />
                <YAxis stroke="#94a3b8" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    border: '1px solid #334155',
                    borderRadius: '0.75rem',
                    color: '#fff',
                    fontSize: '12px',
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar
                  dataKey="average_latency"
                  fill="#6366f1"
                  name="Latência (ms)"
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-slate-400 text-sm">
              Sem dados de latência registrados no período
            </div>
          )}
        </div>
      </div>

      {/* Top Clients Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Clients by Requests */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-xs">
          <h3 className="text-base font-bold text-slate-900 dark:text-white mb-4">
            Top Clientes por Volume de Pedidos
          </h3>
          {metrics?.top_clients_by_volume && metrics.top_clients_by_volume.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-400 uppercase font-semibold">
                    <th className="pb-3">Cliente</th>
                    <th className="pb-3 text-right">Pedidos</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                  {metrics.top_clients_by_volume.map((c) => (
                    <tr key={c.client_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="py-2.5">
                        <Link
                          to={`/clients/${c.client_id}`}
                          className="text-primary-600 dark:text-primary-400 hover:underline font-semibold"
                        >
                          {c.company_name}
                        </Link>
                      </td>
                      <td className="py-2.5 text-right font-mono font-medium text-slate-700 dark:text-slate-300">
                        {c.request_count.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-400 text-center py-8 text-xs">Sem dados disponíveis</p>
          )}
        </div>

        {/* Top Clients by Revenue */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-xs">
          <h3 className="text-base font-bold text-slate-900 dark:text-white mb-4">
            Top Clientes por Receita
          </h3>
          {metrics?.top_clients_by_revenue && metrics.top_clients_by_revenue.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-400 uppercase font-semibold">
                    <th className="pb-3">Cliente</th>
                    <th className="pb-3 text-right">Receita Consolidada</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                  {metrics.top_clients_by_revenue.map((c) => (
                    <tr key={c.client_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="py-2.5">
                        <Link
                          to={`/clients/${c.client_id}`}
                          className="text-primary-600 dark:text-primary-400 hover:underline font-semibold"
                        >
                          {c.company_name}
                        </Link>
                      </td>
                      <td className="py-2.5 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                        ${c.revenue.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-400 text-center py-8 text-xs">Sem dados disponíveis</p>
          )}
        </div>
      </div>

      {/* Endpoint Performance Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-xs">
        <h3 className="text-base font-bold text-slate-900 dark:text-white mb-4">
          Detalhamento de Desempenho por Endpoint
        </h3>
        {metrics?.endpoint_performance && metrics.endpoint_performance.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-400 uppercase font-semibold">
                  <th className="pb-3">Endpoint</th>
                  <th className="pb-3 text-right">Pedidos</th>
                  <th className="pb-3 text-right">Latência Média</th>
                  <th className="pb-3 text-right">Taxa de Erro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                {metrics.endpoint_performance.map((ep, index) => (
                  <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className="py-3 font-mono font-medium text-slate-800 dark:text-slate-200">
                      {ep.endpoint}
                    </td>
                    <td className="py-3 text-right font-mono text-slate-600 dark:text-slate-400">
                      {ep.request_count.toLocaleString()}
                    </td>
                    <td className="py-3 text-right font-mono text-slate-700 dark:text-slate-300 font-semibold">
                      {ep.average_latency.toFixed(0)}ms
                    </td>
                    <td className="py-3 text-right">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${
                          ep.error_rate > 5
                            ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800'
                            : ep.error_rate > 1
                              ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800'
                              : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
                        }`}
                      >
                        {ep.error_rate > 5 && <AlertCircle className="w-3 h-3" />}
                        {ep.error_rate.toFixed(2)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-slate-400 text-center py-8 text-xs">Sem dados disponíveis</p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
